'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useElevarUserData, invalidateElevarContext } from '@/hooks/elevar';
import { IS_PREVIEW } from '@/utils/env';

/**
 * NavigationTracker component that fires Elevar dl_user_data events
 * on initial page load and route changes.
 *
 * This component ensures the base dl_user_data event fires before
 * any other Elevar events, as required by Elevar documentation.
 */
export default function NavigationTracker() {
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);

  // Fire dl_user_data event on mount and route changes
  useElevarUserData();
  useEffect(() => {
    if (IS_PREVIEW) return;
    // Skip on initial mount
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = pathname;
      return;
    }

    // Only process if pathname actually changed
    if (prevPathnameRef.current !== pathname) {
      if (IS_PREVIEW) {
        console.log('[NavigationTracker] Route changed:', prevPathnameRef.current, '→', pathname);
      }

      // Invalidate Elevar context on route changes as per documentation
      invalidateElevarContext();

      // Update the previous pathname
      prevPathnameRef.current = pathname;
    }
  }, [pathname]);
  return null; // This component doesn't render anything
}